import { Gauge } from "lucide-react";
import { tierOf } from "@/lib/impact";

// Compact priority pill (ledger card / alert row): "IMPACT 72 · SEVERE".
export const ImpactBadge = ({ impact, testId }) => {
  if (!impact || typeof impact.score !== "number") return null;
  const t = tierOf(impact);
  return (
    <span
      data-testid={testId}
      title={`Impact = likelihood × consequence × urgency${
        impact.drivers?.length ? " · " + impact.drivers.join(" · ") : ""
      }`}
      className={`inline-flex items-center gap-1 px-1.5 h-5 border ${t.border} ${t.bg} ${t.text} text-[9px] font-mono uppercase tracking-wider`}
    >
      <Gauge className="w-3 h-3" strokeWidth={2} />
      {Math.round(impact.score)}
      <span className="opacity-90">· {t.label}</span>
    </span>
  );
};

// Full impact readout (detail drawer): score, tier, and the likelihood/consequence/urgency drivers.
export const ImpactBlock = ({ impact, testId }) => {
  if (!impact || typeof impact.score !== "number") return null;
  const t = tierOf(impact);
  const bars = [
    ["Likelihood", impact.likelihood],
    ["Consequence", impact.consequence],
    ["Urgency", impact.urgency],
  ];
  return (
    <div className={`mb-4 border ${t.border} ${t.bg} p-3`} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          <Gauge className={`w-3.5 h-3.5 ${t.text}`} />
          <span className={`text-[10px] uppercase tracking-widest font-mono ${t.text}`}>
            Priority Impact Score
          </span>
        </div>
        <div className="flex items-baseline gap-1.5">
          <span className={`text-2xl font-mono leading-none ${t.text}`}>{Math.round(impact.score)}</span>
          <span className={`text-[9px] font-mono uppercase tracking-wider ${t.text}`}>· {t.label}</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-2">
        {bars.map(([label, v]) => (
          <div key={label} className="border border-white/10 bg-white/[0.02] px-2 py-1.5">
            <div className="text-[8px] font-mono uppercase tracking-widest text-atlas-secondary/80">{label}</div>
            <div className="mt-1 h-1 w-full bg-white/10 overflow-hidden">
              <div className="h-full" style={{ width: `${Math.round((v || 0) * 100)}%`, backgroundColor: t.dot }} />
            </div>
            <div className={`text-[10px] font-mono mt-0.5 ${t.text}`}>{Math.round((v || 0) * 100)}%</div>
          </div>
        ))}
      </div>
      {impact.drivers?.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {impact.drivers.map((d, i) => (
            <span key={i} className="text-[9px] font-mono text-atlas-secondary/80 border border-white/10 px-1.5 py-0.5">
              {d}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
